import { useEffect, useState, type ReactNode } from "react";
import {
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Select,
  Textarea,
} from "@fluentui/react-components";
import {
  COST_CATEGORIES,
  COST_CATEGORY_LABEL,
  inEffect,
  PAY_AMOUNT_LABEL,
  PAY_TYPE_LABEL,
  PAY_TYPES,
  STATES,
  TIME_ZONES,
  today,
  validateProfile,
  validateRecord,
  type BonusOn,
  type BonusTargetRecord,
  type BonusType,
  type Person,
  type RecordKind,
  type Role,
} from "../../../core";
import { errorText, send, type PreviewResponse, type Stored } from "../api";
import { DateInput } from "../DateInput";
import { day, delta, money, parseAmount, perHour } from "../format";
import { ErrorBar, Warnings } from "../ui";

type Close = (changed: boolean) => void;

/** What a record dialog needs to know about the person: the card and the bonus dashboard both have this. */
export type PersonRef = { id: string; name: string; _etag: string };

const TITLES: Record<RecordKind, string> = {
  comp: "Change pay",
  costItem: "Add an associated cost",
  capacity: "Change capacity or PTO",
  location: "Change home work state",
  bonusTarget: "Enter a compensation letter",
  bonusOverride: "Override the bonus",
  travel: "Add travel: work in another state",
};

/** A dialog frame: title, body, error line, and Cancel plus one primary action. */
function Frame(props: { title: string; children: ReactNode; error?: string; action: string; busy: boolean; disabled?: boolean; onAction: () => void; onClose: Close; wide?: boolean }) {
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && props.onClose(false)}>
      <DialogSurface className={props.wide ? "dialog-wide" : undefined}>
        <DialogBody>
          <DialogTitle>{props.title}</DialogTitle>
          <DialogContent className="dialog-stack">{props.children}</DialogContent>
          <ErrorBar message={props.error} />
          <DialogActions>
            <Button appearance="secondary" onClick={() => props.onClose(false)}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={props.busy || props.disabled} onClick={props.onAction}>
              {props.busy ? "Saving…" : props.action}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/** Runs a save; reports its error, or closes the dialog as changed. */
function useSave(onClose: Close) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
      onClose(true);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };
  return { busy, error, run };
}

/** The rate effect of a change, fetched from the API before anything is saved (EC6). */
function usePreview(personId: string, body: object | null) {
  const key = body ? JSON.stringify(body) : null;
  const [preview, setPreview] = useState<PreviewResponse>();
  useEffect(() => {
    if (!key) return setPreview(undefined);
    const timer = setTimeout(() => {
      send<PreviewResponse>("POST", `/people/${encodeURIComponent(personId)}/preview`, JSON.parse(key))
        .then(setPreview)
        .catch(() => setPreview(undefined));
    }, 250);
    return () => clearTimeout(timer);
  }, [personId, key]);
  return preview;
}

function PreviewPanel({ preview }: { preview?: PreviewResponse }) {
  if (!preview) return <Caption1 className="muted">The effect on this person's rates appears here once the change is complete.</Caption1>;
  const { before, after } = preview;
  return (
    <div className="preview">
      <Body1>
        <strong>From {day(preview.day)}:</strong> Base {perHour(before.baseRate)} → {perHour(after.baseRate)} ({delta(after.baseRate - before.baseRate, perHour)}); OTE{" "}
        {perHour(before.oteRate)} → {perHour(after.oteRate)} ({delta(after.oteRate - before.oteRate, perHour)}).
      </Body1>
      <Caption1>
        Loaded cost a year: {money(before.loadedBase)} → {money(after.loadedBase)} Base. Every plan week from this date is re-costed
        automatically.
      </Caption1>
      <Warnings items={preview.warnings} />
    </div>
  );
}

export function RecordDialog({ person, kind, onClose }: { person: Stored<Person>; kind: RecordKind; onClose: Close }) {
  const currentPay = inEffect(person.compRecords, today());
  const [f, setF] = useState<Record<string, string>>({
    from: today(),
    type: currentPay?.type ?? "exempt",
    amount: "",
    note: "",
    category: "healthcare",
    label: "",
    frequency: "monthly",
    amortizeMonths: "12",
    to: "",
    hoursPerWeek: String(inEffect(person.capacity, today())?.hoursPerWeek ?? 45),
    ptoDays: String(inEffect(person.capacity, today())?.ptoDays ?? 15),
    state: kind === "travel" ? "" : (inEffect(person.locations, today())?.state ?? ""),
  });
  const set = (k: string) => (_: unknown, d: { value: string }) => setF((x) => ({ ...x, [k]: d.value }));
  const record: Record<string, unknown> =
    kind === "comp"
      ? { from: f.from, type: f.type, amount: parseAmount(f.amount), note: f.note || undefined }
      : kind === "costItem"
        ? {
            from: f.from,
            to: f.to || undefined,
            category: f.category,
            label: f.label,
            amount: parseAmount(f.amount),
            frequency: f.frequency,
            amortizeMonths: f.frequency === "one-time" ? Number(f.amortizeMonths) : undefined,
          }
        : kind === "capacity"
          ? { from: f.from, hoursPerWeek: Number(f.hoursPerWeek), ptoDays: Number(f.ptoDays) }
          : kind === "travel"
            ? { from: f.from, to: f.to, state: f.state, note: f.note || undefined }
            : { from: f.from, state: f.state };
  const problems = validateRecord(kind, record);
  const preview = usePreview(person.id, problems.length ? null : { kind, record });
  const save = useSave(onClose);
  const touched = kind === "comp" || kind === "costItem" ? f.amount !== "" : true;

  return (
    <Frame
      title={`${TITLES[kind]}: ${person.name}`}
      action="Save change"
      busy={save.busy}
      disabled={problems.length > 0}
      error={save.error}
      onClose={onClose}
      wide
      onAction={() => void save.run(() => send("POST", `/people/${encodeURIComponent(person.id)}/records`, { etag: person._etag, kind, record }))}
    >
      <Caption1>
        A change takes effect on its date and never rewrites history. Earlier periods keep the values that applied then.
      </Caption1>
      <div className="form-grid">
        <Field label={kind === "costItem" ? "Starts" : kind === "travel" ? "First day" : "Effective date"} required>
          <DateInput type="date" value={f.from} onChange={set("from")} />
        </Field>
        {kind === "comp" && (
          <>
            <Field label="Pay type" required>
              <Select value={f.type} onChange={set("type")}>
                {PAY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {PAY_TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={PAY_AMOUNT_LABEL[f.type as keyof typeof PAY_AMOUNT_LABEL]} required>
              <Input value={f.amount} onChange={set("amount")} contentBefore="$" inputMode="decimal" autoFocus />
            </Field>
            <Field label="Note">
              <Input value={f.note} onChange={set("note")} placeholder="e.g. annual review" />
            </Field>
          </>
        )}
        {kind === "costItem" && (
          <>
            <Field label="Ends" hint="Optional. Leave open if it continues.">
              <DateInput type="date" value={f.to} onChange={set("to")} />
            </Field>
            <Field label="Category" required>
              <Select value={f.category} onChange={set("category")}>
                {COST_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {COST_CATEGORY_LABEL[c]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Label" required>
              <Input value={f.label} onChange={set("label")} placeholder="e.g. Medical plan" />
            </Field>
            <Field label="Amount" required>
              <Input value={f.amount} onChange={set("amount")} contentBefore="$" inputMode="decimal" />
            </Field>
            <Field label="Frequency" required>
              <Select value={f.frequency} onChange={set("frequency")}>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
                <option value="one-time">One-time, spread over months</option>
              </Select>
            </Field>
            {f.frequency === "one-time" && (
              <Field label="Spread over (months)" required>
                <Input type="number" value={f.amortizeMonths} onChange={set("amortizeMonths")} min={1} max={120} />
              </Field>
            )}
          </>
        )}
        {kind === "capacity" && (
          <>
            <Field label="Hours a week" required>
              <Input type="number" value={f.hoursPerWeek} onChange={set("hoursPerWeek")} min={1} max={80} />
            </Field>
            <Field label="Planned PTO (days a year)" required>
              <Input type="number" value={f.ptoDays} onChange={set("ptoDays")} min={0} max={60} />
            </Field>
          </>
        )}
        {kind === "travel" && (
          <>
            <Field label="Last day" required>
              <DateInput type="date" value={f.to} onChange={set("to")} />
            </Field>
            <Field label="Note">
              <Input value={f.note} onChange={set("note")} placeholder="e.g. client on-site in Austin" />
            </Field>
          </>
        )}
        {(kind === "location" || kind === "travel") && (
          <Field label={kind === "travel" ? "Working in" : "State"} required>
            <Select value={f.state} onChange={set("state")}>
              <option value="">Pick a state</option>
              {STATES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Select>
          </Field>
        )}
      </div>
      {touched && problems.length > 0 && <Warnings items={problems} />}
      <PreviewPanel preview={problems.length ? undefined : preview} />
    </Frame>
  );
}

export function VoidDialog({ person, recordId, label, onClose }: { person: PersonRef; recordId: string; label: string; onClose: Close }) {
  const [reason, setReason] = useState("");
  const preview = usePreview(person.id, { voidId: recordId });
  const save = useSave(onClose);
  return (
    <Frame
      title={`Withdraw ${label}`}
      action="Withdraw"
      busy={save.busy}
      disabled={!reason.trim()}
      error={save.error}
      onClose={onClose}
      onAction={() => void save.run(() => send("POST", `/people/${encodeURIComponent(person.id)}/records/${encodeURIComponent(recordId)}/void`, { etag: person._etag, reason }))}
    >
      <Body1>
        Withdraw a record only if it was entered in error. It stays on the card, marked withdrawn, and stops counting. For a real
        change, add a new record with its own date instead.
      </Body1>
      <Field label="Reason" required>
        <Textarea value={reason} onChange={(_, d) => setReason(d.value)} placeholder="e.g. typed an extra zero" />
      </Field>
      <PreviewPanel preview={preview} />
    </Frame>
  );
}

/** Ends an associated cost or a bonus override on a date; other records change by adding a new one. */
export function EndRecordDialog({ person, recordId, label, noun, onClose }: { person: PersonRef; recordId: string; label: string; noun: "cost" | "override"; onClose: Close }) {
  const [to, setTo] = useState(today());
  const save = useSave(onClose);
  return (
    <Frame
      title={`End ${label}`}
      action={`End ${noun}`}
      busy={save.busy}
      disabled={!to}
      error={save.error}
      onClose={onClose}
      onAction={() => void save.run(() => send("POST", `/people/${encodeURIComponent(person.id)}/records/${encodeURIComponent(recordId)}/end`, { etag: person._etag, to }))}
    >
      {noun === "override" && <Body1>From the day after, the letter's targets count again.</Body1>}
      <Field label={`Last day of the ${noun}`} required>
        <DateInput type="date" value={to} onChange={(_, d) => setTo(d.value)} />
      </Field>
    </Frame>
  );
}

/** The types a dialog offers: active ones, plus any retired one still in use. */
const offeredTypes = (types: readonly BonusType[], keep: readonly string[]) => types.filter((t) => t.active || keep.includes(t.id));

const typeLabel = (t: BonusType) => `${t.name}${t.active ? "" : " (retired)"}${t.includedInOte ? "" : " · not in OTE"}`;

/**
 * BP2: a compensation letter's annual target per bonus type, from a date. It starts from the letter
 * in effect, so a new letter is the old one with its changes.
 */
export function BonusLetterDialog({ person, types, letter, onClose }: { person: PersonRef; types: readonly BonusType[]; letter?: BonusTargetRecord; onClose: Close }) {
  const shown = offeredTypes(types, letter?.targets.map((t) => t.typeId) ?? []);
  const [from, setFrom] = useState(letter ? today() : `${today().slice(0, 4)}-01-01`);
  const [note, setNote] = useState("");
  const [amounts, setAmounts] = useState<Record<string, string>>(() =>
    Object.fromEntries(shown.map((t) => [t.id, String(letter?.targets.find((x) => x.typeId === t.id)?.amount ?? "")])),
  );
  const targets = shown.filter((t) => amounts[t.id]?.trim()).map((t) => ({ typeId: t.id, amount: parseAmount(amounts[t.id]) }));
  const record = { from, targets, note: note.trim() || undefined };
  const problems = validateRecord("bonusTarget", record, new Set(types.map((t) => t.id)));
  const preview = usePreview(person.id, problems.length ? null : { kind: "bonusTarget", record });
  const save = useSave(onClose);
  const total = targets.reduce((s, t) => s + (Number.isFinite(t.amount) ? t.amount : 0), 0);
  return (
    <Frame
      title={`${TITLES.bonusTarget}: ${person.name}`}
      action="Save letter"
      busy={save.busy}
      disabled={problems.length > 0}
      error={save.error}
      onClose={onClose}
      wide
      onAction={() => void save.run(() => send("POST", `/people/${encodeURIComponent(person.id)}/records`, { etag: person._etag, kind: "bonusTarget", record }))}
    >
      <Caption1>
        Enter the annual target the letter states for each bonus type; leave a type blank if the letter doesn't include it. From its
        date the letter replaces the one before. Earlier periods keep the targets that applied then.
      </Caption1>
      <div className="form-grid">
        <Field label="Effective date" required>
          <DateInput type="date" value={from} onChange={(_, d) => setFrom(d.value)} />
        </Field>
        <Field label="Note">
          <Input value={note} onChange={(_, d) => setNote(d.value)} placeholder={`e.g. ${from.slice(0, 4)} compensation letter`} />
        </Field>
        {shown.map((t) => (
          <Field key={t.id} label={typeLabel(t)}>
            <Input value={amounts[t.id] ?? ""} onChange={(_, d) => setAmounts((a) => ({ ...a, [t.id]: d.value }))} contentBefore="$" contentAfter="/yr" inputMode="decimal" />
          </Field>
        ))}
      </div>
      <Body1>
        <strong>Annual bonus at target: {money(total)}</strong>
      </Body1>
      {targets.length > 0 && problems.length > 0 && <Warnings items={problems} />}
      <PreviewPanel preview={problems.length ? undefined : preview} />
    </Frame>
  );
}

/**
 * BP3: replace one type's amount, or the whole bonus, from a date and optionally until one. A
 * whole-bonus override takes precedence over per-type ones.
 */
export function BonusOverrideDialog(props: { person: PersonRef; types: readonly BonusType[]; current?: BonusOn; typeId?: string | null; onClose: Close }) {
  const { person, types, current } = props;
  const shown = offeredTypes(types, current?.lines.map((l) => l.typeId) ?? []);
  const [typeId, setTypeId] = useState<string>(props.typeId === undefined ? (shown[0]?.id ?? "") : (props.typeId ?? ""));
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const record = { from, to: to || undefined, typeId: typeId || null, amount: parseAmount(amount), note: note.trim() || undefined };
  const problems = validateRecord("bonusOverride", record, new Set(types.map((t) => t.id)));
  const preview = usePreview(person.id, problems.length ? null : { kind: "bonusOverride", record });
  const save = useSave(props.onClose);
  const line = current?.lines.find((l) => l.typeId === typeId);
  const now = !current
    ? undefined
    : typeId
      ? line
        ? `The letter's target is ${money(line.target)}${line.override !== null ? `, already overridden to ${money(line.override)}` : ""}.`
        : "The letter in effect has no target for this type."
      : `The bonus counting now is ${money(current.total)}.`;
  return (
    <Frame
      title={`${TITLES.bonusOverride}: ${person.name}`}
      action="Save override"
      busy={save.busy}
      disabled={problems.length > 0}
      error={save.error}
      onClose={props.onClose}
      wide
      onAction={() => void save.run(() => send("POST", `/people/${encodeURIComponent(person.id)}/records`, { etag: person._etag, kind: "bonusOverride", record }))}
    >
      <Caption1>
        An override replaces the amount from its date, and the cost model uses it. A whole-bonus override replaces the total and wins
        over any per-type override. End or withdraw it to return to the letter's targets.
      </Caption1>
      <div className="form-grid">
        <Field label="Applies to" required>
          <Select value={typeId} onChange={(_, d) => setTypeId(d.value)}>
            {shown.map((t) => (
              <option key={t.id} value={t.id}>
                {typeLabel(t)}
              </option>
            ))}
            <option value="">The whole bonus</option>
          </Select>
        </Field>
        <Field label="Annual amount" required hint={now}>
          <Input value={amount} onChange={(_, d) => setAmount(d.value)} contentBefore="$" contentAfter="/yr" inputMode="decimal" autoFocus />
        </Field>
        <Field label="From" required>
          <DateInput type="date" value={from} onChange={(_, d) => setFrom(d.value)} />
        </Field>
        <Field label="Until" hint="Optional. Leave open if it continues.">
          <DateInput type="date" value={to} onChange={(_, d) => setTo(d.value)} />
        </Field>
      </div>
      <Field label="Reason">
        <Input value={note} onChange={(_, d) => setNote(d.value)} placeholder="e.g. sales quota reset for the second half" />
      </Field>
      {amount !== "" && problems.length > 0 && <Warnings items={problems} />}
      <PreviewPanel preview={problems.length ? undefined : preview} />
    </Frame>
  );
}

export function ProfileDialog({ person, roles, onClose }: { person: Stored<Person>; roles: Role[]; onClose: Close }) {
  const [p, setP] = useState({
    name: person.name,
    title: person.title ?? "",
    roleId: person.roleId ?? "",
    email: person.email ?? "",
    timeZone: person.timeZone,
    payIds: person.payIds.join(", "),
  });
  const set = (k: keyof typeof p) => (_: unknown, d: { value: string }) => setP((x) => ({ ...x, [k]: d.value }));
  const profile = { ...p, payIds: p.payIds.split(",").map((s) => s.trim()).filter(Boolean) };
  const problems = validateProfile({ ...profile, status: person.status, endDate: person.endDate });
  const save = useSave(onClose);
  return (
    <Frame
      title="Edit profile"
      action="Save"
      busy={save.busy}
      disabled={problems.length > 0}
      error={save.error}
      onClose={onClose}
      wide
      onAction={() => void save.run(() => send("PATCH", `/people/${encodeURIComponent(person.id)}`, { etag: person._etag, profile }))}
    >
      <div className="form-grid">
        <Field label="Name" required>
          <Input value={p.name} onChange={set("name")} />
        </Field>
        <Field label="Title">
          <Input value={p.title} onChange={set("title")} />
        </Field>
        <Field label="Role">
          <Select value={p.roleId} onChange={set("roleId")}>
            <option value="">None</option>
            {roles.filter((r) => r.active || r.id === person.roleId).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.active ? "" : " (retired)"}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="3Seven account">
          <Input type="email" value={p.email} onChange={set("email")} />
        </Field>
        <Field label="Time zone">
          <Select value={p.timeZone} onChange={set("timeZone")}>
            {TIME_ZONES.map((z) => (
              <option key={z} value={z}>
                {z.replace("America/", "").replace("Pacific/", "").replace("_", " ")}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Pay IDs in old workbooks" hint="Comma-separated, e.g. Carlson_2026-1. Used only to map imported rows.">
          <Input value={p.payIds} onChange={set("payIds")} />
        </Field>
      </div>
      {problems.length > 0 && <Warnings items={problems} />}
    </Frame>
  );
}

export function ArchiveDialog({ person, onClose }: { person: Stored<Person>; onClose: Close }) {
  const restoring = person.status === "archived";
  const [endDate, setEndDate] = useState(person.endDate ?? today());
  const save = useSave(onClose);
  const profile = restoring ? { status: "active" } : { status: "archived", endDate };
  return (
    <Frame
      title={restoring ? `Restore ${person.name}` : `Archive ${person.name}`}
      action={restoring ? "Restore" : "Archive"}
      busy={save.busy}
      disabled={!restoring && !endDate}
      error={save.error}
      onClose={onClose}
      onAction={() => void save.run(() => send("PATCH", `/people/${encodeURIComponent(person.id)}`, { etag: person._etag, profile }))}
    >
      {restoring ? (
        <Body1>Restoring clears the end date and puts {person.name} back into capacity.</Body1>
      ) : (
        <>
          <Body1>
            Archiving keeps all of {person.name}'s history and costs. They leave capacity after their last day, and placeholders stop
            averaging them into role rates.
          </Body1>
          <Field label="Last day of work" required>
            <DateInput type="date" value={endDate} onChange={(_, d) => setEndDate(d.value)} />
          </Field>
        </>
      )}
    </Frame>
  );
}
